import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import history from 'common/history';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import {
  selectBundleTabPage,
  selectCurrentCase,
  selectCurrentFolder,
  selectDocSearchLocation,
  selectDocSearchVal,
  selectDocuments,
  selectGlobalSideBarOpen,
  selectGlobalSummaryOpen,
  selectIsGlobalPageLookup,
  selectMatchedUrl,
  selectPresentQuery,
  selectSpecificParam,
  selectUseBackEndForPresentMode,
  selectUserAiTools,
} from 'common/selectors';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import { openNewBackgroundTab } from 'utils/browser';
import { fileTypes } from '../enums';
import equal from 'react-fast-compare';
import DocumentSearch from '../DocumentSearch';
import FileViewer from '../FileViewer';
import useFolderSpecificFileDetails from './hooks/useFolderSpecificFileDetails';
import ConfidentialityRing from './ConfidentialityRing';
import { usePrevious } from 'utils/hooks';
import { useDispatch } from 'react-redux';
import { setCurrentSelectedFile } from '../redux/setCurrentSelectedFile';
import { useFilteredAndSortedData } from '../redux/setFilteredAndSortedData';
import { Button, IconButton, Tooltip } from '@mui/material';
import T from 'i18n';
import { PhotoFilter, AutoAwesome } from '@mui/icons-material';
import { setSidebarOpen, setSummaryOpen } from 'features/common/redux/navbarLayout';
import GlobalPagingBreadcrumbItem from './GlobalPagingBreadCrumbItem';

type ViewerWrapperProps = {
  fileId: string;
  fileType: string;
  isDocFinder: boolean;
};

export default ({ fileId, fileType, isDocFinder }: ViewerWrapperProps) => {
  const dispatch = useDispatch();
  const isBundleTabPage = useSelector(selectBundleTabPage);
  const caseId = useSelector(selectCurrentCase).id;
  const currentFolder = useSelector(selectCurrentFolder);
  const currentUrl = useSelector(selectMatchedUrl);
  const isTrialBundle = currentFolder?.type === 'trialbook';
  const isGlobalPageLookUp = useSelector(selectIsGlobalPageLookup) as boolean;
  const docSearchVal = useSelector(selectDocSearchVal);
  const docSearchLocation = useSelector(selectDocSearchLocation);
  const present = useSelector(selectPresentQuery);
  const documents = useSelector(selectDocuments) as Array<any>;
  const useBackEndForPresentMode = useSelector(selectUseBackEndForPresentMode);
  const userAiTools = useSelector(selectUserAiTools);
  const file = useSelector((state) => selectSpecificParam(state, 'file'));
  const prevFile = usePrevious(file);
  const { filteredAndSortedData } = useFilteredAndSortedData();
  const requiredDocumentsData = filteredAndSortedData || documents;
  const globalPaging = currentFolder?.globalPaging;
  const isPresentModePage =
    currentUrl.includes(`${caseId}/present-mode`) && !useBackEndForPresentMode;
  const isTagsFolder = currentFolder?.type === 'tags';

  const fileViewerRef = useRef<any>();
  const [searchDetails, setSearchDetails] = useState(null); //TODO: figure out what happened to this
  const [searchFunctions, setSearchFunctions] = useState(null);
  const isSearchMode = currentUrl.includes(`${caseId}/search-mode`);
  const isSearchModeRefreshed = file && documents.length === 0 && isSearchMode;

  const { currentFileMetaData, bundleSpecificFileDetails } = useFolderSpecificFileDetails(
    isTrialBundle,
    globalPaging,
    isGlobalPageLookUp,
    isPresentModePage,
    isTagsFolder,
    isSearchModeRefreshed,
  );
  const searchHandler = useCallback((sd: any) => {
    setSearchDetails((prevSearchDetails: any) => {
      if (!equal(prevSearchDetails, sd)) return sd;
      return prevSearchDetails;
    });
  }, []); // useCallback to prevent rerendering for file viewer

  const searchFunctionsHandler = useCallback((sf: any) => {
    setSearchFunctions(sf);
  }, []); // useCallback to prevent rerendering for file viewer

  useEffect(() => {
    // hide summary when file loads first time
    dispatch(setSummaryOpen(false));
  }, []);

  useEffect(() => {
    if (prevFile && prevFile !== file) {
      setSearchDetails(null);
      setSearchFunctions(null);
    }
  }, [prevFile, file]);
  const openGlobally = useSelector(selectGlobalSideBarOpen);
  const summaryOpen = useSelector(selectGlobalSummaryOpen);

  const sideBarHandler = () => {
    if (openGlobally && !summaryOpen) {
      dispatch(setSidebarOpen(false)); // global
      dispatch(setSummaryOpen(true));
    } else if (!openGlobally && !summaryOpen) {
      dispatch(setSummaryOpen(true));
    } else if (summaryOpen) {
      // If summary is already open, close it
      dispatch(setSummaryOpen(false));
      if (!openGlobally) {
        dispatch(setSidebarOpen(true));
      }
    }
  };
  const isGlobalPaging =
    globalPaging ||
    isGlobalPageLookUp ||
    (bundleSpecificFileDetails && Object.keys(bundleSpecificFileDetails).length > 0) ||
    (isPresentModePage &&
      currentFileMetaData &&
      Object.keys(currentFileMetaData).includes('folderId')) ||
    ((isTagsFolder || isSearchModeRefreshed) &&
      currentFileMetaData &&
      Object.keys(currentFileMetaData).includes('folderId')) ||
    currentFileMetaData?.globalPagePrefix;

  const fileStartPage =
    currentFileMetaData?.startPage && currentFileMetaData?.startPage.includes('.')
      ? currentFileMetaData?.startPage.split('.')
      : currentFileMetaData?.startPage;

  const isStartPageFirstDigitAfterDecimalZero =
    fileStartPage && fileStartPage.length > 1 && fileStartPage[1].startsWith('0');
  const currentFileId =
    currentFileMetaData?.folderFileId ||
    currentFileMetaData?.compositeKey ||
    currentFileMetaData.id ||
    '';
  const currentFileIndex = useMemo(() => {
    // If no current file metadata or required data, return -1
    if (!currentFileMetaData || !requiredDocumentsData?.length) {
      return -1;
    }

    // Step 1: First try to find by folderFileId (highest priority)
    if (currentFileMetaData.folderFileId) {
      const index = requiredDocumentsData.findIndex(
        (doc: any) => doc.folderFileId === currentFileMetaData.folderFileId,
      );
      if (index !== -1) return index;
    }

    // Step 2: Then try to find by compositeKey (second priority)
    if (currentFileMetaData.compositeKey) {
      const index = requiredDocumentsData.findIndex(
        (doc: any) => doc.compositeKey === currentFileMetaData.compositeKey,
      );
      if (index !== -1) return index;
    }

    // Step 3: Finally try to find by id (lowest priority)
    if (currentFileMetaData.id) {
      const index = requiredDocumentsData.findIndex(
        (doc: any) => doc.id === currentFileMetaData.id,
      );
      if (index !== -1) return index;
    }

    return -1;
  }, [requiredDocumentsData, currentFileMetaData]);
  // Determine if buttons should be disabled
  const disableActionBtns = useMemo(
    () => ({
      disablePrev: !currentFileId || currentFileIndex <= 0 || requiredDocumentsData.length <= 1,
      disableNext:
        !currentFileId ||
        currentFileIndex === requiredDocumentsData.length - 1 ||
        requiredDocumentsData.length <= 1,
    }),
    [currentFileId, currentFileIndex, requiredDocumentsData],
  );
  const navigateToDocument = (newFolderFileId: string, newFileId: string, fileData: any) => {
    const baseUrl = history.location.pathname.replace(/\/files.*/, '');
    const presentParam = present ? `&present=${present}` : '';
    // Construct the new URL with the target document ID
    const newUrl = `${baseUrl}/files/${newFileId}${presentParam ? `?${presentParam.substring(1)}` : ''}`;
    // Update the URL and dispatch the current selected file
    history.push(newUrl);

    setTimeout(() => {
      dispatch(
        setCurrentSelectedFile({
          ...fileData,
        }),
      );
    }, 0);
    documents.forEach((doc: any) => {
      if (doc.folderFileId === newFolderFileId && doc.id === newFileId) {
        doc.selected = true;
      } else {
        doc.selected = false;
      }
    });
  };

  const prevClicked = () => {
    if (disableActionBtns.disablePrev) return;

    const prevFileData = requiredDocumentsData[currentFileIndex - 1];
    navigateToDocument(prevFileData?.folderFileId, prevFileData?.id, prevFileData);
  };

  const nextClicked = () => {
    if (disableActionBtns.disableNext) return;
    const nextFileData = requiredDocumentsData[currentFileIndex + 1];
    navigateToDocument(nextFileData?.folderFileId, nextFileData?.id, nextFileData);
  };
  return (
    <div className="case-viewer-wrapper">
      <Container fluid className="h-100">
        <Row className="justify-content-between align-items-center p-2 px-3">
          <Breadcrumb>
            <Breadcrumb.Item active={true}>{fileId}</Breadcrumb.Item>
            {isBundleTabPage ? (
              <Breadcrumb.Item active={true} className="no-separator">
                {currentFileMetaData.globalPagePrefix
                  ? `${currentFileMetaData.globalPagePrefix} / ${currentFileMetaData.tab}`
                  : ''}
              </Breadcrumb.Item>
            ) : (
              <GlobalPagingBreadcrumbItem
                isGlobalPaging={isGlobalPaging}
                currentFileMetaData={currentFileMetaData}
                fileStartPage={fileStartPage}
                isStartPageFirstDigitAfterDecimalZero={isStartPageFirstDigitAfterDecimalZero}
              />
            )}
            <ConfidentialityRing confidentialityRing={currentFileMetaData?.confidentialityRing} />
          </Breadcrumb>
          {(docSearchVal || docSearchLocation) &&
            searchFunctions &&
            fileViewerRef.current &&
            currentFileMetaData &&
            (fileId === currentFileMetaData.id ||
              (currentFileMetaData.file && fileId === currentFileMetaData.file.id)) && (
              <DocumentSearch
                searchFunctions={searchFunctions}
                docSearchVal={docSearchVal}
                docSearchLocation={docSearchLocation}
                isAiTools={currentUrl.includes(`${caseId}/ai-tools`)}
                isSearchMode={isSearchMode}
                sideSummaryOpen={summaryOpen}
              />
            )}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            <div className="actions-buttons">
              {userAiTools && !currentUrl.includes(`${caseId}/ai-tools`) ? (
                <Tooltip title={T.translate('case.askAiTab')} enterDelay={0} leaveDelay={0} arrow>
                  <IconButton
                    onClick={() => sideBarHandler()}
                    size="small"
                    sx={{
                      color: '#4567e5',
                      opacity: 0.75,
                      marginRight: '0.3rem',
                      padding: '0 0 2px 0',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0)',
                        color: '#4567e5',
                        opacity: 1,
                      },
                    }}
                  >
                    <span
                      style={{
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '3px',
                      }}
                    >
                      <AutoAwesome />
                      {T.translate('case.ai')}
                    </span>
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title={summaryOpen ? '' : T.translate('filters.searchThisDocument')}>
                  <PhotoFilter
                    onClick={() => {
                      sideBarHandler();
                    }}
                    style={{ width: 20.4, height: 22, marginBottom: 2.5 }}
                  />
                </Tooltip>
              )}

              <Tooltip
                title={T.translate('case.openDocInNewTab')}
                enterDelay={0}
                leaveDelay={0}
                arrow
              >
                <IconButton
                  sx={{
                    color: '#212529',
                    fontSize: 'inherit',
                    padding: '0 0 2px 0',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0)',
                    },
                  }}
                >
                  <FontAwesomeIcon
                    onClick={() => {
                      const goToPage = fileViewerRef.current?.getCurrentPageNumber() || 1;
                      if (fileViewerRef.current?.pausePlayer) fileViewerRef.current.pausePlayer();
                      if (present) {
                        if (fileViewerRef.current?.stopPresent) fileViewerRef.current.stopPresent();
                      }
                      const isGPExistCurrentFileMetaData = currentFileMetaData?.globalPagePrefix;
                      let urlWithParams = '';

                      if (fileType === fileTypes.mp4) {
                        if (present) {
                          urlWithParams = `&present=${present}`;
                        } else {
                          urlWithParams = '';
                        }
                      } else {
                        if (isGPExistCurrentFileMetaData && currentFileMetaData && present) {
                          // When metadata exists and present is true, include present param.
                          urlWithParams = `&tab=${currentFileMetaData.tab}&startPage=${currentFileMetaData.startPage}&pageCount=${currentFileMetaData.pageCount}&globalPagePrefix=${currentFileMetaData.globalPagePrefix}&present=${present}`;
                        } else if (
                          isGPExistCurrentFileMetaData &&
                          currentFileMetaData &&
                          !present
                        ) {
                          // When metadata exists but present is falsy, exclude present param.
                          urlWithParams = `&tab=${currentFileMetaData.tab}&startPage=${currentFileMetaData.startPage}&pageCount=${currentFileMetaData.pageCount}&globalPagePrefix=${currentFileMetaData.globalPagePrefix}`;
                        } else if (present && !isGPExistCurrentFileMetaData) {
                          urlWithParams = `&present=${present}`;
                        } else {
                          urlWithParams = '';
                        }
                      }
                      openNewBackgroundTab(
                        `/case/${caseId}/files/${fileId}?folderId=${currentFolder?.id || currentFileMetaData?.bundleLocations[0]?.folderId || currentFileMetaData?.folderId}${currentFileMetaData?.folderFileId ? `&folderFileId=${currentFileMetaData?.folderFileId}` : ''}${
                          goToPage > 1 ? `&goToPage=${goToPage}` : ''
                        }${urlWithParams}`,
                      );
                    }}
                    icon={faExternalLinkAlt}
                  />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={T.translate('case.closePreviewSideBar')}
                enterDelay={0}
                leaveDelay={0}
                arrow
              >
                <IconButton
                  sx={{
                    color: '#212529',
                    fontSize: 'inherit',
                    padding: '0 2px 0 0',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0)',
                    },
                  }}
                >
                  <FontAwesomeIcon
                    onClick={() => {
                      history.push(history.location.pathname.replace(/\/files.*/, ``));
                      dispatch(setCurrentSelectedFile(null));
                      dispatch(setSummaryOpen(false));
                      if (!openGlobally) dispatch(setSidebarOpen(true));
                    }}
                    icon={faTimesCircle}
                  />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </Row>
        <Row className="secondRow">
          <Col md={12} className="h-100">
            <Card>
              {currentFileMetaData && Object.keys(currentFileMetaData).length > 0 && (
                <FileViewer
                  ref={fileViewerRef}
                  bundleSpecificFileDetails={bundleSpecificFileDetails}
                  isDocFinder={isDocFinder}
                  searchHandler={searchHandler}
                  searchFunctionsHandler={searchFunctionsHandler}
                  currentFileMetaData={currentFileMetaData}
                  sideSummaryOpen={summaryOpen}
                  setSideSummaryOpen={(isOpen: boolean) => dispatch(setSummaryOpen(isOpen))}
                />
              )}
            </Card>
          </Col>
        </Row>
        <Row className="thirdRow">
          <Col md={12} className="h-100">
            <div className="navigation-buttons-container">
              <>
                <Button
                  id="updateFilePrevButton"
                  onClick={prevClicked}
                  variant="text"
                  size="small"
                  sx={{
                    color: '#4567e5',
                    '&:hover': {
                      color: '#4567e5',
                    },
                  }}
                  disabled={disableActionBtns.disablePrev}
                >
                  {T.translate('generic.prevDoc')}
                </Button>
                <Button
                  id="updateFileNextButton"
                  onClick={nextClicked}
                  variant="text"
                  size="small"
                  sx={{
                    color: '#4567e5',
                    '&:hover': {
                      color: '#4567e5',
                    },
                  }}
                  disabled={disableActionBtns.disableNext}
                >
                  {T.translate('generic.nextDoc')}
                </Button>
              </>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};
